import type { Document, DocumentLink } from 'BrokerTsLib/src/dtos'
import type { ApiLogDetail, ApiLogSimpleItem } from 'BrokerTsLib/src/dtos/Api/ApiLog'
import type { GenericQueryOptions } from 'BrokerTsLib/src/hooks'
import { longStaleTime } from 'BrokerTsLib/src/hooks'
import apiClient from 'BrokerTsLib/src/Utils/apiClient'
import { useQuery } from 'react-query'

import { Product } from 'BrokerTsLib/src/enums'
import { useUrl } from '.'
import { Apis } from '../appSettings/AppSettings'
import { useApp } from './useApp'

export const useGetAllDocuments = (paramsId: string | undefined | null, insurerOfferId: number, options?: { enabled?: boolean }) => {
  const url = useUrl(Apis.document)
  const { product } = useApp()

  return useQuery({
    queryKey: ['GetAllDocuments', paramsId, insurerOfferId, product],
    queryFn: async () => {
      const res = await apiClient.get<DocumentLink[]>(`${url}/GetAllDocuments`, { params: { paramsId: paramsId ?? '', insurerOfferId } })

      if (!Array.isArray(res.data))
        throw new Error('Invalid response data.')

      return res.data
    },
    retry: 3,
    retryDelay: (count) => count * 1000,
    ...options
  })
}

export const useGetOfferDocuments = (offerId: number, options?: GenericQueryOptions<Document[]>) => {
  const url = useUrl(Apis.document)

  return useQuery({
    queryKey: ['GetOfferDocuments', offerId],
    queryFn: () => apiClient.get<Document[]>(`${url}/GetOfferDocuments?offerId=${offerId}`),
    retry: false, ...options
  })
}

export const useGetApiLog = (apiLogId: number) => {
  const url = useUrl(Apis.document, Product.VehicleInsurance)

  return useQuery({
    queryKey: ['GetApiLog', apiLogId],
    queryFn: async () => {
      const res = await apiClient.get<ApiLogDetail>(`${url}/GetApiLog?apiLogId=${apiLogId}`)
      return res.data
    },
    retry: false
  })
}

export const useGetApiLogs = (paramsId: string, options?: GenericQueryOptions<ApiLogSimpleItem[]>) => {
  const url = useUrl(Apis.document)

  return useQuery({
    queryKey: ['GetApiLogs', paramsId],
    queryFn: () => apiClient.get<ApiLogSimpleItem[]>(`${url}/GetApiLogs?paramsId=${paramsId}`),
    retry: false, ...options
  })
}

export const useGetBrandLogo = (categoryId?: number | null, brand?: string | null, options?: GenericQueryOptions<string>) => {
  const url = useUrl(Apis.document)

  return useQuery({
    queryKey: ['GetBrandLogo', categoryId, brand],
    queryFn: () => apiClient.get<string>(`${url}/GetBrandLogoBase64?categoryId=${categoryId}&brand=${brand}`),
    staleTime: longStaleTime,
    refetchOnMount: false,
    retry: false,
    ...options
  })
}

export type VehicleIdentificationLinkPropsDto = {
  paramsId?: string
  spz?: string | null
  vin?: string | null
  vehicleId?: string
}
